/** Magyar tartalom. A modok nevei a `hu.json` katalogust kovetik. */
import type { LanguageContent } from '../registry.js';

export const HU: LanguageContent = {
  home: {
    intro: [
      'A Retusch online rajzos játék 2-10 játékosnak, a böngészőben fut, telepítés és fiók nélkül. Minden körben kapsz egy festményt, amelyről letöröltek egy területet: fesd újra úgy, hogy eltűnjön a műben. Aztán megfordulnak a szerepek, és a többiek retusait kell kiszúrnod.',
      'Szinte minden rajzos játék azt jutalmazza, akit a leghamarabb felismernek. A Retusch az ellenkezőjét kéri: az nyer, akit nem vesznek észre.',
    ],
    steps: [
      {
        title: 'Egy festmény lyukkal',
        text: 'Kapsz egy illusztrációt, amelyről letöröltek egy területet.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Újrafested',
        text: 'A területet úgy rajzolod meg, hogy beleolvadjon a műbe.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'A vadászat',
        text: 'A festmény mindenki retusával együtt tér vissza: találd meg őket a többiek előtt.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'A leleplezés',
        text: 'Minden rejtekhely előkerül, és hullanak a pontok.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Közös vadászat',
        text: 'Mindenki ugyanarra a festményre fest, ki-ki a saját területére. Aztán egyszerre indul a vadászat mindenkinek: egy retust csak egyszer lehet megtalálni, az, aki elsőként kiszúrja. Ez az alapértelmezett mód, ebben történik folyamatosan valami.',
      },
      {
        name: 'Stream',
        text: 'A házigazda nem rajzol: egyedül keres, miközben mindenki más az ő festményében rejtőzik el. Élő adásra találták ki, ahol maga a vadászat a műsor. Örökre ingyenes.',
      },
      {
        name: 'Mindenkinek saját festménye',
        text: 'Játékosonként egy mű. Egyetlen ember retusát keresed, egyetlen kattintással. A játék legegyszerűbb szabálya, és a legfeszültebb.',
      },
      {
        name: 'Egyvalaki rajzol',
        text: 'Egy kör, egy rajzoló, a többiek pedig hajtóvadászatban. A szerep körbejár, a rajzoló pedig nézi, ahogy a rejtekhelye elesik vagy kitart.',
      },
      {
        name: 'Battle royale',
        text: 'Ugyanaz a vadászat, de az elsőként felfedezett retus kiejti a szerzőjét. Az utolsó talpon maradóig megy, és közben egyetlen pontot sem számol.',
      },
      {
        name: 'Verseny',
        text: 'Senki nem keres. Mindenki ugyanannak a festménynek ugyanazt a területét festi újra, aztán mindenki arra a retusra szavaz, amelyik a legjobban tetszik neki. Minden kapott szavazat egy pontot ér.',
      },
    ],
    faq: [
      {
        question: 'Hány játékos kell hozzá?',
        answer:
          '2-től 10-ig. Ha egyedül vagy, egy automatikus ellenfél foglalja el az üres helyet, hogy a játék azonnal induljon. Négy főtől felfelé mutatja meg a közös vadászat a legjobb arcát.',
      },
      {
        question: 'Ingyenes?',
        answer:
          'Igen. A gyors játék, a közös vadászat és a stream mód ingyenes, időkorlát és játékszám-korlát nélkül. A többi mód is ingyenes, egyelőre.',
      },
      {
        question: 'Kell hozzá fiók?',
        answer:
          'Nem. Beírsz egy nevet, kattintasz, játszol. A fiók csak annyit tesz, hogy megőrzi a nevet és az avatart egyik játékról a másikra: továbbra sem kötelező.',
      },
      {
        question: 'Működik telefonon?',
        answer:
          'Igen, a felület alkalmazkodik az érintéshez: összecsukott eszköztár, nagyító az ujj alatt, dupla koppintás a nagyításhoz. Ettől függetlenül egérrel és nagy képernyőn pontosabban lehet rajzolni.',
      },
      {
        question: 'Hogyan hívom meg a barátaimat?',
        answer:
          'Létrehozol egy szobát, kimásolod a meghívó linkjét, és bemásolod, ahová akarod. A barátaid egyenesen az asztalnál landolnak, fiók és telepítés nélkül.',
      },
      {
        question: 'Mi az a „retus”?',
        answer:
          'Az, amit a letörölt területre festesz. Ha sikerül, az eredeti festmény darabjának tűnik, és senki nem találja meg. Ha nem sikerül, kiszúrja a szemedet.',
      },
    ],
    headings: {
      intro: 'Rajzos játék, amelyben el kell tűnni',
      steps: 'Hogyan játsszuk',
      modes: 'Hat játékmód',
      faq: 'Gyakori kérdések',
      more: 'Tovább',
    },
    links: {
      rules: 'Olvasd el a teljes szabályokat és a pontozást',
      modes: 'Hasonlítsd össze a módokat részletesen',
    },
  },

  howToPlay: {
    title: 'Hogyan játsszunk a Retusch-csal',
    summary:
      'A Retusch teljes szabályai: egy kör menete, a pontozás mindkét oldalon, a rajzeszközök, valamint tanácsok az elrejtőzéshez és a kereséshez.',
    sections: [
      {
        heading: 'Az ötlet három mondatban',
        blocks: [
          'Kapsz egy festményt, amelyről letöröltek egy területet, és úgy fested újra, hogy észrevétlen maradjon.',
          'Aztán mindenki a retusált festményeket nézi, és keresi az újrafestett területeket.',
          'Pontot szerzel azzal, ha gyorsan találsz, és azzal is, amíg a rejtekhelyed kitart.',
        ],
      },
      {
        heading: 'Egy kör menete',
        blocks: [
          'Egy játék egyforma köröket fűz össze, alapértelmezés szerint ötöt. Mindegyik három szakaszon megy át.',
          {
            list: [
              'A rajzolás. Megkapod a festményedet és a területedet, és alapértelmezés szerint 2 perced van újrafesteni. A Kész gomb leadást jelent: amint mindenki leadta, a kör anélkül halad tovább, hogy megvárná az órát.',
              'A vadászat. A festmény újra megjelenik a retusokkal, és alapértelmezés szerint 30 másodperced van kiszúrni őket. Egy kattintás egy újrafestett területre az egész asztal előtt felfedi azt.',
              'A leleplezés. Minden rejtekhely keretet és nevet kap, azok is, amelyeket senki nem látott meg. Egy csúszkával összevetheted a retusált festményt az eredeti művel.',
            ],
          },
        ],
      },
      {
        heading: 'A pontozás',
        blocks: [
          'Két számláló fut egyszerre, és szembemennek egymással: aki keres, gyors akar lenni; aki rajzolt, azt akarja, hogy a területe kitartson.',
          {
            list: [
              'A keresőnek: 1000 pont, ha az első 3 másodpercben megtalálod, majd egyenletes csökkenés a szakasz végi 100 pontig. A tévesztés és a semmit sem találás egyaránt semmit nem ér.',
              'A rajzolónak: csak 100 pont, ha a területed azonnal elesik, majd egyenletes növekedés 800 pontig, ha a végéig kitart. Az a retus, amelyet senki nem talál meg, a maximumot éri, 800 pontot.',
            ],
          },
          'A rajzoló csak egyszer kap fizetséget, a leleplezéskor, mégpedig a leggyorsabb kereső alapján. Így nem számít, hogy ketten keresnek-e vagy nyolcan: csak az számít, ki talál meg elsőként.',
          'A pontozás ugyanaz, bármilyen hosszúra állítod a szakaszt. A vadászat megnyújtása nem mozdítja el a két végpontot, csak a köztük lévő meredekséget.',
          'Körönként egyetlen hiba. Az első kattintás, amely egyetlen retust sem talál el, lezárja a vadászatodat: e szabály nélkül találomra át lehetne fésülni a képet, amíg véletlenül bele nem futunk egybe. Két mozdulat nem kerül semmibe: a saját területre kattintás, és a már megtalált retusra kattintás.',
        ],
      },
      {
        heading: 'A rajzeszközök',
        blocks: [
          {
            list: [
              'Az ecset (B): három gyors méret, plusz egy csúszka a pontos mérethez. Az 1-es, 2-es és 3-as gyorsbillentyű választja ki a három méretet.',
              'A festékesvödör (P): egy mozdulattal kitölt egy egyszínű felületet, és soha nem lép ki a területedről.',
              'A pipetta (I): a festmény bármely pontjáról felvesz egy színt. Az Alt lenyomva tartása egy kattintás erejéig átvált rá, aztán visszaadja az előző eszközt.',
              'A kéz (H): mozgatja a nézetet. A területeden kívül az ecset és a festékesvödör ugyanezt teszi, így szinte soha nem kell eszközt váltanod.',
              'Visszavonás (Ctrl+Z vagy Cmd+Z): vonásonként lép vissza, akár harmincszor. Újra végrehajtás nincs.',
              'A háttérszín a paletta végén: ez a területed kiindulási színe. Ezzel festeni annyi, mint törölni, azzal a különbséggel, hogy ez is vonás, mint a többi, amit vissza lehet vonni és le lehet fedni. Ezért nincs radír a játékban.',
            ],
          },
        ],
      },
      {
        heading: 'Öt tanács az elrejtőzéshez',
        blocks: [
          {
            list: [
              'A színeket vedd fel pipettával, ne találgasd őket. A pipetta a környezet pontos színét adja: egy csak kicsit is mellétalált folt messziről látszik, a pontos viszont láthatatlanná válik.',
              'Hosszabbítsd meg azt, ami átszeli a területedet. Egy falnak, egy horizontnak, egy ágnak, amely az egyik oldalon belép, a másikon pontosan a helyén kell kilépnie. A megszakadt vonal az első nyom, amit egy tapasztalt játékos keres.',
              'Először a sarkokat fesd meg. Ez a leggyakoribb hiba: a közép gondosan kidolgozva, a négy sarok a kiindulási háttéren maradva, és a négyzet szabad szemmel látszik anélkül, hogy a rajzra egyáltalán ránéznénk.',
              'Haladj az egésztől a részlet felé. Először vastag ecsettel tedd le a nagy színfoltokat, aztán finomíts. Fordítva az összes idődet egyetlen sarok emészti fel, míg a többi üres marad.',
              'Másold le a környezet részletességét. Egy túl tiszta terület egy zsúfolt részlet közepén ugyanúgy látszik, mint egy elnagyolt egy egyszínű felület közepén.',
            ],
          },
        ],
      },
      {
        heading: 'Négy tanács a kereséshez',
        blocks: [
          {
            list: [
              'Keresd a vonalakat, amelyek megszakadnak. Egy perem, egy kábel, egy asztalsarok, amely hajszálnyit elcsúszik: szinte mindig ott van.',
              'Előbb pásztázz, aztán nagyíts. Teljes nézetben a rosszul beleolvadó terület foltnak látszik. A nagyítás megerősítésre való, nem keresésre.',
              'Gyanakodj a túl simára. Egy tökéletesen egyenletes felület a textúra közepén ugyanolyan gyanús, mint egy elnagyolt folt.',
              'Ne kattints találomra. Egyetlen hibára van jogod: jobb három másodpercet veszíteni ellenőrzéssel, mint az egész kört.',
            ],
          },
        ],
      },
      {
        heading: 'És módonként?',
        blocks: [
          'Mindaz, ami fentebb áll, a közös vadászatot írja le, az alapértelmezett módot. A többi mód azon változtat, hogy ki rajzol, ki keres és hogyan lehet nyerni: mindegyik részletei a játékmódok oldalán vannak.',
        ],
      },
    ],
  },

  modes: {
    title: 'A Retusch játékmódjai',
    summary:
      'A Retusch hat játékmódja: közös vadászat, stream, mindenkinek saját festménye, egyvalaki rajzol, battle royale és verseny. Menet, ajánlott létszám és játékidő.',
    sections: [
      {
        heading: 'Közös vadászat',
        blocks: [
          'Az alapértelmezett mód, és az, amellyel érdemes kezdeni.',
          'Mindenki ugyanarra a festményre rajzol, ki-ki a saját területére. Amikor a retusok leadásra kerültek, a mű egészben tér vissza, és egyszerre indul a vadászat mindenkinek. Egy retust csak egyszer lehet megtalálni, az, aki elsőként kiszúrja: ekkor megjelenik az egész asztalnak, és kikerül a játékból. A sajátodat soha nem találod meg, és az a rejtekhely, amelyet senki nem lát meg, a maximumot adja a szerzőjének.',
          'Nagy csapatnak ideális: nincsenek holt pillanatok, mindenki egyszerre keres, és minden felfedezés mindenki képernyőjén megjelenik.',
          'Ajánlott: 3-10 játékos.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'Az „egyvalaki rajzol” tükörképe, élő adásra kitalálva.',
          'Minden játékos ugyanarra a festményre rajzol, a házigazda kivételével. Aztán egyedül a házigazda keres, miközben a közönsége nézi, ahogy szenved. Nem szerez pontot, és nem szerepel a rangsorban: a játék azok között zajlik, akik elrejtőznek, ő vezeti a vadászatot. Annyiszor tévedhet, ahányszor akar, az egyetlen hiba szabálya rá nem vonatkozik.',
          'Élő adásos estére ideális, mégpedig egy pontos okból: mivel a házigazda nem rajzol, a nézők soha nem látják felépülni a rejtekhelyét. A többi módban végignézik, ahogy megfesti a saját területét, és a vadászatnak nincs többé titka előttük.',
          'Ajánlott: 4-10 játékos. Ingyenes, és az is marad.',
        ],
      },
      {
        heading: 'Mindenkinek saját festménye',
        blocks: [
          'Minden játékos megkapja a saját művét, és elrejti benne a retusát. A vadászatkor egyetlen másik ember festményét kapod meg, és egyetlen kattintásod van megmutatni az újrafestett területet. Semmi verseny, semmi hajsza: csak te, egy festmény és egy döntés.',
          'Kettesben ideális, vagy amikor nyugodt játékot szeretnétek, amelyben mindenki ráér.',
          'Ajánlott: 2-10 játékos.',
        ],
      },
      {
        heading: 'Egyvalaki rajzol',
        blocks: [
          'Egy kör, egy rajzoló, a többiek pedig hajtóvadászatban.',
          'Minden körben egyetlen ember kap festményt. A többiek várnak, aztán az ő retusát keresik, mindenkit a saját ideje alapján értékelve. A rajzolót a leggyorsabb kereső alapján értékelik, ő pedig kimerevítve látja viszont a rejtekhelyét, miközben keresik. A szerep körbejár: itt a körök beállítása teljes forgókat számol, mert a játék csak akkor igazságos, ha mindenki ugyanannyiszor rajzolt.',
          'Kis csapatnak ideális, amely azonos feltételek mellett akarja összemérni magát, rajzról rajzra.',
          'Ajánlott: 3-6 játékos, e fölött hosszúvá válik a várakozás.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'A közös vadászat, körönként egy kieséssel.',
          'Ugyanaz a közös festmény, ugyanaz az egyidejű vadászat. Itt viszont egyetlen pontot sem számolunk: az egyetlen kérdés az, ki marad. Az a játékos, akinek a retusát elsőként fedezik fel, elhagyja a játékot. A többiek a kör végéig tovább keresnek, ami már senkit nem ejt ki, de mindenkinek megadja a helyezését, a saját területén megjelenítve. Ha senkit nem találnak meg, senki nem esik ki.',
          'Este lezárásához ideális: a feszültség nő, ahogy az asztal kiürül, és az utolsó kört ketten játsszák.',
          'Ajánlott: 4-10 játékos.',
        ],
      },
      {
        heading: 'Verseny',
        blocks: [
          'Az egyetlen mód, amelyben senki nem keres.',
          'Mindenki ugyanannak a festménynek ugyanazt a területét festi újra. Aztán mindenki a saját tempójában végignézi a többiek retusait, és arra szavaz, amelyik a legjobban sikerültnek tűnik. Minden kapott szavazat egy pontot ér a szerzőjének. A szavazás alatt a szerzők névtelenek, és magára senki nem szavazhat: a nevek és a szavazatok csak a leleplezéskor kerülnek elő.',
          'Akkor ideális, ha a csapat vegyes, vagy ha valaki inkább a vadászat órájának nyomása nélkül rajzolna.',
          'Ajánlott: 3-10 játékos.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Közös vadászat', players: '3-10', duration: '15-20 perc' },
    { name: 'Stream', players: '4-10', duration: '15-20 perc' },
    { name: 'Mindenkinek saját festménye', players: '2-10', duration: '15-20 perc' },
    { name: 'Egyvalaki rajzol', players: '3-6', duration: '20-35 perc' },
    { name: 'Battle royale', players: '4-10', duration: '20-30 perc' },
    { name: 'Verseny', players: '3-10', duration: '10-15 perc' },
  ],
  modeTableHeadings: ['Mód', 'Játékosok', 'Játékidő'],
  premium: {
    heading: 'Ingyenes és fizetős módok',
    blocks: [
      'A közös vadászat és a stream mód ingyenes, véglegesen.',
      'A másik négy a játék LÉTREHOZÁSAKOR válik fizetőssé, és csak ott. A csatlakozás mindig ingyenes marad: elég, ha az asztalnál egyvalaki feloldotta a játékot, és már mindenki játszhat minden móddal, fiók és fizetés nélkül.',
      'A feloldás egyszeri vásárlás lesz, előfizetés nélkül, és a hirdetéseket is leveszi arról a fiókról, amelyik elvégezte. Addig minden mód nyitva áll mindenki előtt.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Mi az a Retusch?',
      date: '2026-08-21',
      summary:
        'Online rajzos játék többeknek, formájában közel a Gartic Phone-hoz, de a mechanikája inkább a Meccha Chameleonra emlékeztet: ahelyett, hogy felismernének, eltűnni próbálsz.',
      sections: [
        {
          heading: 'Rajzos játék, amelyben a felismerés vereség',
          blocks: [
            'A Retusch online rajzos játék 2-10 játékosnak. Minden körben kapsz egy illusztrációt, amelyről letöröltek egy területet, és úgy kell újrafestened, hogy beleolvadjon a műbe. Aztán mindenki a retusált festményeket nézi, és keresi az újrafestett területeket.',
            'Ami megkülönbözteti, egyetlen mondatba fér: az nyer, akit nem vesznek észre.',
          ],
        },
        {
          heading: 'Amiben osztozik a Gartic Phone-nal',
          blocks: [
            'A forma ugyanaz: megnyitsz egy linket, beírsz egy nevet, játszol. Semmi fiók, semmi telepítés, semmi letöltendő kliens. A játék a böngészőben fut, és egy parti pár másodperc alatt indul. A barátaidat úgy hívod meg, hogy bemásolsz egy linket egy beszélgetésbe, és megtelik az asztal.',
            'A ritmus is ismerős: rövid körök, egy óra, egy rajzolási szakasz, aztán egy szakasz, amelyben kiderül, mit gyártottak a többiek. És legfőképpen az a közös leleplezés a kör végén, amikor mindenki kommentálja, amit lát. Ugyanaz a csapatos öröm.',
            'Ahogy a Gartic Phone-nál, itt sem kell tudni rajzolni. Egy jól letett égdarab veri a mesterművet, amelyik kilóg.',
          ],
        },
        {
          heading: 'Amit a Meccha Chameleonból vesz',
          blocks: [
            'A mélyén viszont máshol van a mechanika. A Meccha Chameleonban, amely háromdimenziós játék a Steamen, kifested a saját karakteredet, aztán elhelyezed egy díszletben, abban a reményben, hogy senki nem különbözteti meg a környezetétől. A győzelem a hasonlóságból és a jó elhelyezésből fakad, nem az akcióból.',
            'A Retusch átveszi ezt az ötletet, és visszahozza a képhez. Nem te választod meg, hová bújsz, a területet a játék adja; cserébe teljes egészében te gyártod azt, ami belekerül. A rejtekhely akkor sikerült, ha a tekintet megállás nélkül csúszik át rajta.',
            'És mint minden mimikrijátékban, a feszültséget a kettős szerep adja. Egyszerre vagy rejtőzködő és vadász: miközben te fürkészed a festményt egy gyanús folt után kutatva, valaki a tiédet fürkészi.',
          ],
        },
        {
          heading: 'Ami sehol máshol nincs',
          blocks: [
            'A pontozás a két oldalt ellentétesen jutalmazza. Aki keres, annál több pontot kap, minél hamarabb talál; aki rajzolt, annál többet, minél tovább tart ki a területe. Az a retus, amelyet senki nem lát meg, a maximumot adja a szerzőjének.',
            'Ebből egy nagyon sajátos játékmód születik: nem szépet akarsz csinálni, hanem pontosat. Felvenni a jó színt, pont a helyén meghosszabbítani egy vonalat, egyetlen sarkot sem hagyni üresen. Ezek megfigyelési mozdulatok, még mielőtt rajzolási mozdulatok lennének.',
            'Hogy a gyakorlatban is megértsd, a legegyszerűbb elolvasni a teljes szabályokat, vagy elindítani egy gyors játékot: beállítás és várakozás nélkül indul.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Hogyan legyél verhetetlen a Retusch-ban',
      date: '2026-08-21',
      summary:
        'A gyorsbillentyűk, az egésztől a részlet felé haladó módszer és az üres sarkok hibája: három dolog, amely elválasztja a kezdőt attól a játékostól, akit már nem találnak meg.',
      sections: [
        {
          heading: 'Tanuld meg a gyorsbillentyűket, és megduplázod a hasznos időt',
          blocks: [
            'Két perc rajzolás nagyon gyorsan elszalad, ha minden eszközért egérrel kell menni. A gyorsbillentyűk könnyedén visszahoznak húsz-harminc másodpercet körönként, és pontosan azok a másodpercek jutnak majd a részletekre.',
            {
              list: [
                'B az ecsethez, P a festékesvödörhöz, I a pipettához, H a kézhez.',
                '1, 2 és 3 a három ecsetmérethez, az eszköztár érintése nélkül.',
                'Ctrl+Z, Macen Cmd+Z, a vonásonkénti visszavonáshoz.',
                'Az Alt lenyomva tartása pipettára vált, és visszaadja az előző eszközt, amint elengeded. Ez a leghasznosabb mind közül: menet közben veszel fel egy színt anélkül, hogy egyáltalán eszközt váltanál.',
              ],
            },
            'Egy megjegyzés az AZERTY billentyűzetekhez: az 1-es, 2-es és 3-as billentyű Shift nélkül működik, még akkor is, ha a billentyűzeteden &, é és " van rájuk írva. A játék a billentyű helyzetét olvassa, nem a karaktert, amelyet előállít.',
          ],
        },
        {
          heading: 'Fess az egésztől a részlet felé',
          blocks: [
            'A klasszikus kezdő hiba az, hogy egy sarokból indulsz, és azt csiszolgatod. Lejár az óra, a terület háromnegyede még mindig háttér, és a retus tíz méterről látszik.',
            'A jó módszer a fordítottja. Kezdd vastag ecsettel, vagy a festékesvödörrel, és tedd le a nagy színfoltokat: az eget, a falat, a földön az árnyékot. Húsz másodperc alatt a területed már a helyes összbenyomást kelti, és nem olvasható többé idegen tömbként.',
            'Csak ezután csökkentsd a méretet, és foglalkozz az átmenetekkel: az átszelő horizontvonallal, egy tárgy peremével, a színátmenettel, amely félmagasságban változik. Ha kifutsz az időből, részletet veszítesz, nem a kört.',
            'A színeket mindig pipettával vedd fel, ahelyett, hogy a palettáról választanád őket. Egy hozzávetőleges árnyalat elég ahhoz, hogy elárulja az egész területet, míg a pontos árnyalat láthatatlanná teszi.',
          ],
        },
        {
          heading: 'Soha ne hagyd üresen a sarkokat',
          blocks: [
            'Ez az egyes számú nyom, és az első, amit a tapasztalt játékosok keresnek. Az a terület, amelynek a közepe szépen ki van dolgozva, de a négy sarka a háttérszínen maradt, tökéletes téglalapot rajzol a képernyőre. Még a rajzra sem kell ránézni: a mértani forma kiszúrja a szemet.',
            'Szokj rá, hogy először a szélét dolgozod ki, és csak utána a közepét. Egy jól beleolvadó keret megbocsát egy hozzávetőleges belsőt; fordítva ez soha nem igaz.',
            'Menj szándékosan túl a széleken. A játék levágja a vonásodat a terület határánál, tehát semmit nem kockáztatsz azzal, ha jóval tovább mész, és ez az egyetlen módja annak, hogy biztos legyél: egyetlen háttérpixel sem éli túl valamelyik sarokban.',
          ],
        },
        {
          heading: 'És amikor rajtad a keresés sora',
          blocks: [
            'Előbb pásztázd végig a teljes festményt nagyítás nélkül: a rosszul beleolvadó terület foltnak látszik, a nagyítás pedig csak megerősítésre való. Aztán kövesd a hosszú vonalakat, azokat, amelyek átszelik a képet: egy hajszálnyit elcsúsztatott perem szinte mindig retus.',
            'És ne feledd, hogy körönként egyetlen hibára van jogod. Három másodperc ellenőrzés kevesebbe kerül, mint az egész kör.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Rajzos játék baráti estére',
      date: '2026-08-21',
      summary:
        'Miért működik jól a Retusch kis csapatban, hogyan hívj meg másokat egy link bemásolásával, és miért elég egyetlen játékos ahhoz, hogy az egész asztalnak feloldja a fizetős módokat.',
      sections: [
        {
          heading: 'Játék, amely senkitől nem kér semmit',
          blocks: [
            'Egy távoli játékest legnagyobb akadálya a telepítés. Létrehozandó fiók, letöltendő kliens, verzió, amely nem stimmel: a csapat fele feladja, mielőtt játszani kezdenétek.',
            'A Retusch a böngészőben fut. A barátaid rákattintanak egy linkre, beírnak egy nevet, és már az asztalnál is vannak. Nincs mit telepíteni, nincs mit létrehozni, és ugyanolyan jól működik számítógépen, mint telefonon.',
            'Még valami, ami csapatban számít: senki nem marad ki azért, mert nem tud rajzolni. A játék nem azt kéri, hogy ábrázolj valamit, csak azt, hogy egy területet illessz hozzá ahhoz, ami körülveszi. Aki rosszul rajzol, de jól figyel, gyakran nyer.',
          ],
        },
        {
          heading: 'A meghívás a gyakorlatban',
          blocks: [
            'Hozz létre egy szobát a kezdőképernyőről. Vedd ki a pipát a „nyilvános szoba” mellől, ha magatok között akartok maradni: a linkes meghívás lesz ekkor az egyetlen bejárat.',
            'Másold ki a linket a megfelelő gombbal, aztán illeszd be a csoportos csevegésetekbe, a hangcsatornátokra vagy az üzenetküldőtökbe. Tartalmazza a szoba kódját: a barátaid egyenesen a te asztalodnál landolnak.',
            'A játékot beállíthatod, miközben gyűlnek az emberek: a módot, a körök számát, a rajzolási időt, a terület méretét, sőt a képkategóriákat is. Semmi nincs kőbe vésve, amíg el nem indítod.',
          ],
        },
        {
          heading: 'Egy fizet, mindenki játszik',
          blocks: [
            'Két mód örökre ingyenes: a közös vadászat, vagyis az alapértelmezett, és a stream mód.',
            'A többi fizetőssé válik, de csak a játék LÉTREHOZÁSAKOR. A csatlakozás ingyenes marad, feltétel nélkül. A gyakorlatban elég, ha a csapatból egyvalaki feloldotta a játékot, és az egész asztal élvezheti az összes módot, fiók nélkül és fizetés nélkül.',
            'A feloldás egyszeri vásárlás, előfizetés nélkül.',
          ],
        },
        {
          heading: 'Melyik mód melyik csapatnak',
          blocks: [
            'Hárman-négyen a közös vadászat bőven elég, és egy kör alatt meg lehet érteni. Hat főtől felfelé válik igazán jóvá: a felfedezések egymást érik, és a képernyő nem áll le mozogni.',
            'Egy este lezárásához próbáljátok ki a battle royale-t: az asztal körről körre ürül, és az utolsót ketten játsszák. Vegyes csapatnak, ahol valaki inkább nyugodtan rajzolna, a verseny leveszi a vadászat órájának minden nyomását.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Rajzos játék élő adásba',
      date: '2026-08-21',
      summary:
        'A Retusch-nak van élő adásra kitalált módja, amelyben a streamer egyedül keres, miközben a közönsége elrejtőzik. Hogyan használd, és mely más módok működnek közönség előtt.',
      sections: [
        {
          heading: 'Kifejezetten erre készült mód',
          blocks: [
            'A stream mód megfordítja a szerepeket. Minden játékos ugyanarra a festményre rajzol, a házigazda kivételével, aki egyáltalán nem rajzol. Aztán egyedül a házigazda keres, miközben a közönsége nézi.',
            'Pontosan ez az a helyzet, amelyet egy élő adás megkíván, mégpedig egy pontos okból: mivel a házigazda nem rajzol, a rejtekhelye nem létezik, és a csevegés nem látott semmit, amit nem kellett volna. A nézők viszont ismerik a többi játékos rejtekhelyeit, és végignézhetik, ahogy háromszor egymás után elmegy mellettük.',
            'Erre az esetre két szabály íródott. A streamer nem szerez pontot, és nem szerepel a rangsorban: a játék azok között zajlik, akik elrejtőznek, ő vezeti a műsort. És annyiszor tévedhet, ahányszor akar, míg az összes többi mód megáll az első hibánál. Egy élő adás nem érhet véget egy szerencsétlen kattintáson.',
            'A stream mód ingyenes, és az is marad.',
          ],
        },
        {
          heading: 'A verseny, a másik jó ötlet',
          blocks: [
            'A verseny módban mindenki ugyanannak a festménynek ugyanazt a területét festi újra, aztán mindenki arra a retusra szavaz, amelyik a legjobban tetszik neki. Nincs többé mit megtalálni, csak megítélni.',
            'Közönség előtt kiválóan működik, mégpedig ugyanazért, mint a stream mód: nincs mit megtalálni, tehát nincs mit a csevegés elronthatna. A pályaműveket egymás mellett lehet összevetni, a szavazás ad egy pillanatnyi izgalmat, a nevek leleplezése pedig határozott lezárást kínál. Ez egyben a legrövidebb mód is.',
          ],
        },
        {
          heading: 'És a többi mód',
          blocks: [
            'Semmi nem kötelez a kifejezetten erre való módra, de akkor érdemesebb barátokkal játszani, nem a csevegéssel. Minden más módban a streamer a képernyőn festi meg a saját területét: a nézők látják felépülni, és tudják a választ, még mielőtt a vadászat elkezdődne.',
            'Egy baráti parti viszont kívülről nagyon jól követhető: a rajzolási szakasz megmutatja, ahogy mindenki felépíti a rejtekhelyét, a leleplezés pedig egyszerre tárja fel az összes területet, a szerző nevével.',
          ],
        },
        {
          heading: 'Hogyan hozd be a csevegést a játékba',
          blocks: [
            'A meghívó linkje az egyetlen, amit meg kell osztani. Illeszd be a csevegésbe, és az elsőként érkezők megtöltik az asztalt: nincs létrehozandó fiók, nincs letöltés, nincs sorban állás.',
            'A létszámkorlát a szobában állítható, 2 és 10 között. Nagy közönségnél inkább nyiss több rövid partit egyetlen hosszú helyett: egy kör kevesebb mint három percig tart.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Újdonságok a Retusch-ban',
      date: '2026-08-21',
      summary:
        'A Retusch játékbeli változásai, a legfrissebbtől a legrégebbiig: új módok, új szabályok, fejlesztések a rajzolásban és a vadászatban.',
      sections: [
        {
          heading: '2026. augusztus',
          blocks: [
            {
              list: [
                'Az újrafestendő terület már nem fehér négyzetből indul, hanem annak az uralkodó színéből, amit eltakar. Nem megy el többé az idő fele egy olyan háttér újrafestésével, amely a festményen nem is létezik.',
                'A festékesvödör már nem hagy világos szegélyt a vonások mentén: mostantól belecsúszik a lágyított peremük alá.',
                'A battle royale már nem számol pontot. Az elsőként felfedezett játékos elhagyja a játékot, mindenki pedig kap egy körbeli helyezést, a saját területén megjelenítve, pirostól a zöldig.',
                'A stream mód annyi próbálkozást ad a streamernek, amennyit akar: az első hiba már nem zárja le a kört.',
                'Néző módban mostantól mozgatható és nagyítható az a festmény, amelynek az épülését figyeled.',
                'Az emoji reakciók a szerzőjük neve mellől indulnak a játékoslistában, ahelyett, hogy a képernyő aljáról szállnának fel.',
                'Az újrafestendő területen kívül az ecset és a festékesvödör mozgatja a nézetet: a mozgáshoz már nem kell a kéz eszközt elővenni.',
                'A játék neve a bal felső sarokban parti közben megerősítés után visszavisz a kezdőoldalra.',
                'A japán és a koreai csatlakozik a már elérhető tizenöt nyelvhez.',
              ],
            },
          ],
        },
        {
          heading: '2026. július',
          blocks: [
            {
              list: [
                'Új verseny mód: mindenki ugyanazt a területet festi újra, aztán mindenki arra a retusra szavaz, amelyik a legjobban tetszik neki.',
                'Új stream mód, élő adásra kitalálva: a házigazda egyedül keres, miközben a közönsége elrejtőzik.',
                'A képkategóriák felváltják a nehézségi szinteket. Bepipálod, amit látni akarsz, és minden kategória mutat egy példafestményt.',
                'Két egymást követő kör már nem esik ugyanarra a festményre, és a bepipált kategóriák körről körre váltakoznak.',
                'A játék végi galériában visszajátszható egy retus vonásról vonásra, és exportálható képként vagy animált GIF-ként.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'Röviden',
    allArticles: 'Minden cikk',
    theRules: 'A játék szabályai',
    theModes: 'A játékmódok',
  },
};
